/** FPS cap gate for the animation loop. Pure; the loop passes `performance.now()` values. */

export interface FrameGateState {
  /** Phase anchor of the last rendered frame (not the raw timestamp, see below). */
  last: number;
}

/** Frames arriving this much before the interval still count as on time (display jitter). */
const TOLERANCE_MS = 0.5;

/**
 * Standard requestAnimationFrame limiter: render when at least one interval has elapsed since
 * the last rendered frame, then keep the phase (`now - elapsed % interval`) so a display that
 * runs slightly fast or slow does not accumulate drift. A long stall renders once and then
 * waits a full interval: there is never a burst of catch-up frames.
 */
export function frameGate(
  st: FrameGateState,
  now: number,
  capFps: 0 | 60 | 120,
): { render: boolean; last: number } {
  if (capFps === 0) return { render: true, last: now };
  const interval = 1000 / capFps;
  const elapsed = now - st.last;
  if (elapsed < interval - TOLERANCE_MS) return { render: false, last: st.last };
  const phase = elapsed % interval;
  // A frame that is marginally early (within the tolerance) has phase ~interval; treat as on time.
  const last = phase > interval - TOLERANCE_MS ? now : now - phase;
  return { render: true, last };
}
